export const Currency = {
  SEK: 'SEK',
  EUR: 'EUR',
  DKK: 'DKK',
};

export function CurrencySymbol(currencyCode: string): string {
  switch (currencyCode) {
    case Currency.SEK:
      return 'kr';
    case Currency.EUR:
      return '€';
    case Currency.DKK:
      return 'dkr';
  }
}

/**
 * Gets the number of relevant decimals for the given currency. (Example: 2 for EUR, 0 for SEK.)
 * @param currencyCode The currency code (from the 'Currency' object).
 * @returns The number of relevant decimals to preserve when rounding.
 */
export function RoundToDecimals(currencyCode: string): number {
  return currencyCode === Currency.EUR ? 2 : 0; // see "Currency.RoundToDecimals" in backend
}

export type MoneyLike = {
  amount: number;
  currencyCode: string;
};

// We have a mismatch of structure between frontend and backend for the Money class. By recognizing this type in the frontend we can fix errors securely in the project.
export type MoneyBackend = {
  amount: number;
  currency: { isoCode: string };
};

// If the structure of the Money object coming from the backend doesn't match our defined frontend standard, we can securely map it with this check. -- Nick & Atlan
export function isMoneyBackend(obj: any): obj is MoneyBackend {
  return obj && typeof obj.amount === 'number' && typeof obj.currency?.isoCode === 'string';
}

export function isMoneyLike(obj: any): obj is MoneyLike {
  return obj && typeof obj.amount === 'number' && typeof obj.currencyCode === 'string';
}

export function isMoney(obj: any): obj is Money {
  return isMoneyLike(obj) && typeof (obj as Money).round === 'function';
}

export class Money implements MoneyLike {
  constructor(currency: string, amount: number) {
    this.currencyCode = currency;
    this.amount = amount;
  }

  /**
   * Half-way away from zero rounding.
   * @param amount The amount to round.
   * @param decimalPlaces The number of decimal places to round to. Must be an integer!
   */
  static Round(amount: number, decimalPlaces: number): number {
    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0) {
      throw new Error('decimalPlaces must be a non-negative integer.');
    }
    const num1 = Number(`${amount}e${decimalPlaces}`);
    const num2 = amount < 0 ? -Math.round(-num1) : Math.round(num1);
    const num3 = Number(num2 + `e-${decimalPlaces}`);
    return num3;
  }

  /**
   * Half-way away from zero rounding to the specified currency's relevant decimal places.
   * (Example: 2 for EUR, 0 for SEK)
   * @param amount The amount to round.
   * @param currencyCode The currency code (from the 'Currency' object).
   */
  static RoundByCurrency(amount: number, currencyCode: string): number {
    const roundToDecimals = RoundToDecimals(currencyCode);
    return Money.Round(amount, roundToDecimals);
  }

  /**
   * Half-way away from zero rounding to the currency's relevant decimal places.
   * (Example: 2 for EUR, 0 for SEK)
   */
  round(): Money;
  /**
   * Half-way away from zero rounding.
   * @param decimalPlaces The number of decimal places to round to. Must be an integer!
   */
  round(decimalPlaces: number): Money;
  round(decimalPlaces?: number): Money {
    if (decimalPlaces === undefined) {
      decimalPlaces = RoundToDecimals(this.currencyCode);
    }
    return new Money(this.currencyCode, Money.Round(this.amount, decimalPlaces));
  }

  equals(value: any) {
    if (!isMoneyLike(value)) {
      return false;
    }
    return value.amount === this.amount && value.currencyCode === this.currencyCode;
  }

  currencyCode: string;
  amount: number;

  get IsPositive() {
    return this.amount > 0;
  }
  get IsNegative() {
    return this.amount < 0;
  }
  get IsZero() {
    return this.amount == 0;
  }

  toString() {
    let prefixCurrencies = [Currency.EUR];
    let prefix = prefixCurrencies.find(c => c === this.currencyCode) ? CurrencySymbol(this.currencyCode) + ' ' : '';
    let suffix = prefixCurrencies.find(c => c === this.currencyCode) ? '' : ' ' + CurrencySymbol(this.currencyCode);
    return `${prefix}${this.amount}${suffix}`;
  }
}

export class MoneyOptional extends Money {
  constructor();
  constructor(money: MoneyLike);
  constructor(currency: string, amount: number);
  constructor(currencyOrMoney?: string | MoneyLike, amount?: number) {
    if (currencyOrMoney === undefined) {
      super('', amount || 0);
      this.hasValue = false;
    } else if (isMoneyLike(currencyOrMoney)) {
      super(currencyOrMoney.currencyCode, currencyOrMoney.amount);
      this.hasValue = true;
    } else {
      super(currencyOrMoney, amount);
      this.hasValue = true;
    }
  }

  public readonly hasValue?: boolean;
}
